import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import './AdvanceChartWidget.css';

import {
  ComparisonItem,
  DrawingToolConfig,
  DrawingToolKey,
  IntervalKey,
  MockStock,
  PeriodKey,
  TAItem,
} from './types';

import {
  COMPARISON_COLORS,
  DEFAULT_DRAWING_CONFIG,
  DEFAULT_PERIODS,
  DrawingToolsList,
} from './constants';

import { clampPeriod } from './helpers';
import { getChartHtsData } from '../../api/advanceChart.api';
import { calcFromDate, formatDateYMD } from '../../hooks/data/useChartData';

import {
  ChartSettingsProvider,
  useChartSettings,
} from './ChartSettingsContext';
import ChartView, { ChartViewHandle, DrawMenu } from './components/ChartView';
import DrawingToolBox from './components/DrawingToolBox';
import SearchBar from './components/SearchBar';
import Toolbar, { PanelId } from './components/Toolbar';

import ChartTypeSettings from './components/settings/ChartTypeSettings';
import ComparisonSettings from './components/settings/ComparisonSettings';
import DrawingToolsSettings from './components/settings/DrawingToolsSettings';
import GeneralSettings from './components/settings/GeneralSettings';
import TechnicalAnalysisSettings from './components/settings/TechnicalAnalysisSettings';
import TimePeriodSettings from './components/settings/TimePeriodSettings';
import ViewSettings from './components/settings/ViewSettings';

import Layout from '../../Components/Common/Layout/Layout';
import useContainerWidth from '../../hooks/useContainerWidth';
import useResizeNotifier from '../../hooks/useResizeNotifier';
import { BaseWidgetProps, WidgetHeightMessage } from '../../types/common.type';
import {
  ThemeType,
  WidgetMessageType,
  WidgetNameSource,
} from '../../utils/type';

export interface AdvanceChartWidgetProps extends BaseWidgetProps {
  token?: string;
  symbol?: string;
  chartBundleUrl?: string;
  onHeightChange?: (message: WidgetHeightMessage) => void;
}

function getRicCountryCode(ric: string): string | undefined {
  const suffix = ric.split('.').pop()?.toUpperCase() ?? '';
  if (['AX', 'ASX', 'CXA'].includes(suffix)) return 'AUS';
  if (['O', 'N', 'A', 'OQ'].includes(suffix)) return 'USA';
  if (['L', 'IL'].includes(suffix)) return 'GBR';
  if (['T', 'OS'].includes(suffix)) return 'JPN';
  if (suffix === 'HK') return 'HKG';
  if (suffix === 'S') return 'CHE';
  if (suffix === 'PA') return 'FRA';
  if (suffix === 'DE') return 'DEU';
  if (suffix === 'SI') return 'SGP';
  if (suffix === 'TO') return 'CAN';
  return undefined;
}

const buildDefaultDrawingConfigs = (): Record<
  DrawingToolKey,
  DrawingToolConfig
> =>
  DrawingToolsList.reduce(
    (acc, { key }) => ({ ...acc, [key]: { ...DEFAULT_DRAWING_CONFIG } }),
    {} as Record<DrawingToolKey, DrawingToolConfig>,
  );

const AdvanceChartWidgetInner: React.FC<AdvanceChartWidgetProps> = (props) => {
  const { theme = ThemeType.Direct, onHeightChange, token = '' } = props;

  const {
    interval,
    period,
    chartType,
    candlestickColors,
    lineConfig,
    barConfig,
    activeTA,
    view,
    setChartInterval,
    setChartPeriod,
    setChartType,
    setCandlestickColors,
    setLineConfig,
    setBarConfig,
    setActiveTA,
    setView,
    resetToDefault,
  } = useChartSettings();

  const [selectedStock, setSelectedStock] = useState<MockStock>({
    symbol: props.symbol ?? '',
    name: '',
    exchange: '',
    price: 0,
    change: 0,
    changePct: 0,
  });

  const [activeTool, setActiveTool] = useState<DrawingToolKey | null>(null);
  const [moveEnabled, setMoveEnabled] = useState(true);
  const [drawingConfigs, setDrawingConfigs] = useState<
    Record<DrawingToolKey, DrawingToolConfig>
  >(buildDefaultDrawingConfigs);

  const [comparisonMode, setComparisonMode] = useState(false);
  const [comparisonList, setComparisonList] = useState<ComparisonItem[]>([]);

  const [activePanel, setActivePanel] = useState<PanelId | null>(null);
  const [drawMenu, setDrawMenu] = useState<DrawMenu | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  useEffect(() => {
    if (drawMenu) setActivePanel(null);
  }, [drawMenu]);

  const containerRef = useRef<HTMLDivElement>(null);
  const chartViewRef = useRef<ChartViewHandle>(null);

  const sendToChart = (payload: Record<string, unknown>) => {
    chartViewRef.current?.sendToChart(payload);
  };

  useResizeNotifier(
    containerRef,
    WidgetNameSource.AdvanceChart,
    onHeightChange,
  );
  const { isSmallScreen: isMobile } = useContainerWidth(containerRef);

  const [isSearchOpen, setIsSearchOpen] = useState(false);

  useEffect(() => {
    const handler = (e: MouseEvent | TouchEvent) => {
      const target = (
        e instanceof TouchEvent ? e.touches[0]?.target : e.target
      ) as HTMLElement;
      if (!target) return;
      if (target.closest('[data-outside-ignore]')) return;
      if (target.closest('.MuiColorInput-Popover')) return;
      if (containerRef.current && !containerRef.current.contains(target)) {
        setActivePanel(null);
        return;
      }
      if (target.closest('.toolbar-panel-anchor')) return;
      if (target.closest('.toolbar-button-wrapper')) return;
      if (target.closest('.search-bar')) return;
      setActivePanel(null);
    };
    document.addEventListener('mousedown', handler as EventListener);
    document.addEventListener('click', handler as EventListener);
    return () => {
      document.removeEventListener('mousedown', handler as EventListener);
      document.removeEventListener('click', handler as EventListener);
    };
  }, []);

  const handlePanelToggle = useCallback((id: PanelId) => {
    setActivePanel((prev) => (prev === id ? null : id));
    setIsSearchOpen(false);
    setDrawMenu(null);
  }, []);

  const handleIntervalChange = (newInterval: IntervalKey) => {
    setChartInterval(newInterval);
    if (DEFAULT_PERIODS[newInterval]) {
      setChartPeriod(DEFAULT_PERIODS[newInterval]!);
    } else {
      setChartPeriod(clampPeriod(period, newInterval));
    }
  };

  const handleDrawingConfigChange = (
    tool: DrawingToolKey,
    config: DrawingToolConfig,
  ) => {
    setDrawingConfigs((prev) => ({ ...prev, [tool]: config }));
  };

  const handleTAAdd = (item: TAItem) => {
    setActiveTA((prev) => [...prev, item]);
  };

  const handleTARemove = (key: string) => {
    setActiveTA((prev) => {
      const next = prev.filter((t) => t.key !== key);
      if (next.length === 0) setActivePanel(null);
      return next;
    });
  };

  const handleTAUpdate = (key: string, changes: Partial<TAItem>) => {
    setActiveTA((prev) =>
      prev.map((t) => (t.key === key ? { ...t, ...changes } : t)),
    );
  };

  const handleComparisonAdd = (item: ComparisonItem) => {
    setComparisonList((prev) => [...prev, item]);
  };

  const handleComparisonRemove = (id: string) => {
    setComparisonList((prev) => prev.filter((c) => c.id !== id));
  };

  const handleComparisonUpdate = (
    id: string,
    changes: Partial<ComparisonItem>,
  ) => {
    setComparisonList((prev) =>
      prev.map((c) => (c.id === id ? { ...c, ...changes } : c)),
    );
  };

  const { symbol } = selectedStock;

  const RC_MKT: Record<string, string> = {
    's&p500': '.SPX',
    asx200: '.AXJO',
    nasdaq: '.IXIC',
  };
  const EXTERNAL_TA_KEYS = new Set(['RC']);

  const externalTAFetchKey = useMemo(
    () =>
      activeTA
        .filter((t) => EXTERNAL_TA_KEYS.has(t.key))
        .map((t) => {
          const sv = t.selectValues ?? {};
          return 'RC:' + (sv[1] ?? 'S&P500');
        })
        .sort()
        .join('|'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      activeTA.map((t) => t.key + JSON.stringify(t.selectValues)).join(),
      symbol,
    ],
  );

  useEffect(() => {
    const specials = activeTA.filter((t) => EXTERNAL_TA_KEYS.has(t.key));
    if (specials.length === 0 || !symbol) return;

    let cancelled = false;
    const today = new Date();
    const fromDate = calcFromDate(period, today);
    const toYMD = formatDateYMD(today);
    const fromYMD = formatDateYMD(fromDate);

    specials.forEach((ta) => {
      const ric =
        RC_MKT[(ta.selectValues?.[1] ?? 'S&P500').toLowerCase()] ?? '.SPX';
      getChartHtsData(ric, interval, fromYMD, toYMD, token)
        .then((data) => {
          if (!cancelled) handleTAUpdate(ta.key, { externalData: '\n' + data });
        })
        .catch(() => {
          /* show indicator without data on API error */
        });
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalTAFetchKey, symbol, interval, period, token]);

  const handleComparisonModeToggle = (enabled: boolean) => {
    setComparisonMode(enabled);
    if (enabled) {
      setDrawMenu(null);
      setComparisonList((prev) => {
        if (prev.find((c) => c.symbol === symbol)) return prev;
        return [
          {
            id: `${symbol}-main`,
            symbol,
            name: selectedStock.name,
            assetType: 'stock',
            color: COMPARISON_COLORS[0],
            thickness: 2,
            style: 's',
            countryCode: getRicCountryCode(symbol),
          },
          ...prev,
        ];
      });
      setView((v) => ({ ...v, showTrendlines: false }));
    } else {
      setView((v) => ({ ...v, showTrendlines: true }));
    }
  };

  const handleClearAll = () => {
    setActivePanel(null);
    setShowClearConfirm(true);
  };

  const executeClearAll = () => {
    sendToChart({ deleteAllLineAction: true, lineEditDone: true });
    setActiveTA([]);
    setDrawingConfigs(buildDefaultDrawingConfigs());
    setActiveTool(null);
    setMoveEnabled(true);
    setComparisonList([]);
    setComparisonMode(false);
    setDrawMenu(null);
    setShowClearConfirm(false);
  };

  const handleResetDefault = () => {
    sendToChart({
      deleteAllLineAction: true,
      resizeChartAction: true,
      lineEditDone: true,
    });
    resetToDefault();
    setDrawingConfigs(buildDefaultDrawingConfigs());
    setActiveTool(null);
    setMoveEnabled(true);
    setComparisonMode(false);
    setComparisonList([]);
    setDrawMenu(null);
    setActivePanel(null);
  };

  const handlePrint = () => {
    setActivePanel(null);
    if (chartViewRef.current?.print()) return;
    // Bundle served from another origin — its document can't be printed directly
    console.warn(
      '[AdvanceChartWidget] chart bundle is cross-origin; printing the whole page instead',
    );
    window.print();
  };

  return (
    <Layout theme={theme}>
      <div className="chart-widget-container" ref={containerRef}>
        {/* Top row: Search + Toolbar */}
        <div className="chart-top-row">
          {(!isMobile || isSearchOpen) && (
            <SearchBar
              selectedSymbol={symbol}
              onSelect={setSelectedStock}
              token={token}
            />
          )}
          <div className="chart-toolbar-column">
            <Toolbar
              activePanel={activePanel}
              taCount={activeTA.length}
              comparisonCount={comparisonList.length}
              onPanelToggle={handlePanelToggle}
              isMobile={isMobile}
              isSearchOpen={isSearchOpen}
              onSearchToggle={() => {
                setIsSearchOpen((v) => !v);
                setActivePanel(null);
              }}
              panelContents={{
                timePeriod: (
                  <TimePeriodSettings
                    selectedInterval={interval}
                    selectedPeriod={period}
                    onIntervalChange={handleIntervalChange}
                    onPeriodChange={setChartPeriod}
                  />
                ),
                chartType: (
                  <ChartTypeSettings
                    selectedType={chartType}
                    candlestickColors={candlestickColors}
                    lineConfig={lineConfig}
                    barConfig={barConfig}
                    onTypeChange={setChartType}
                    onCandlestickColorsChange={setCandlestickColors}
                    onLineConfigChange={setLineConfig}
                    onBarConfigChange={setBarConfig}
                  />
                ),
                drawingTools: (
                  <DrawingToolsSettings
                    activeTool={activeTool}
                    moveEnabled={moveEnabled}
                    drawingConfigs={drawingConfigs}
                    onToolChange={setActiveTool}
                    onMoveToggle={setMoveEnabled}
                    onConfigChange={handleDrawingConfigChange}
                  />
                ),
                technicalAnalysis: (
                  <TechnicalAnalysisSettings
                    activeTA={activeTA}
                    onAdd={handleTAAdd}
                    onRemove={handleTARemove}
                    onUpdate={handleTAUpdate}
                  />
                ),
                comparison: (
                  <ComparisonSettings
                    comparisonMode={comparisonMode}
                    comparisonList={comparisonList}
                    onToggleMode={handleComparisonModeToggle}
                    onAdd={handleComparisonAdd}
                    onRemove={handleComparisonRemove}
                    onUpdate={handleComparisonUpdate}
                    token={token}
                  />
                ),
                view: <ViewSettings view={view} onChange={setView} />,
                settings: (
                  <GeneralSettings
                    onClearAll={handleClearAll}
                    onResetDefault={handleResetDefault}
                    onPrint={handlePrint}
                  />
                ),
              }}
            />
          </div>
        </div>

        {/* ── Chart area ── */}
        <div className="chart-body" onClick={() => setActivePanel(null)}>
          {showClearConfirm && (
            <div
              className="clear-confirm-overlay"
              onClick={() => setShowClearConfirm(false)}
            >
              <div
                className="clear-confirm-dialog"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="clear-confirm-message body-m-regular">
                  Are you sure you want to clear all?
                </span>
                <div className="clear-confirm-actions">
                  <button
                    className="clear-confirm-btn clear-confirm-btn--cancel body-m-regular"
                    onClick={() => setShowClearConfirm(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="clear-confirm-btn clear-confirm-btn--ok body-m-regular"
                    onClick={executeClearAll}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          )}
          {drawMenu && (
            <DrawingToolBox
              color={`#${drawMenu.color}`}
              dash={drawMenu.dash}
              thickness={drawMenu.thickness}
              mode={drawMenu.mode}
              onColorChange={(c) => {
                sendToChart({ lineColorChangeAction: c.replace('#', '') });
                setDrawMenu((m) =>
                  m ? { ...m, color: c.replace('#', '') } : null,
                );
              }}
              onStyleChange={(s) => {
                sendToChart({ lineStyleChangeAction: s });
                setDrawMenu((m) => (m ? { ...m, dash: s } : null));
              }}
              onThicknessChange={(t) => {
                sendToChart({ lineThicknessChangeAction: t });
                setDrawMenu((m) => (m ? { ...m, thickness: t } : null));
              }}
              onDelete={() => {
                sendToChart({ deleteLineAction: true });
              }}
              onCopy={() => sendToChart({ copyLineAction: true })}
              onFinishEditing={() => {
                sendToChart({ drawTool: 'move', lineEditDone: true });
                setDrawMenu(null);
                setActiveTool(null);
                setMoveEnabled(true);
              }}
            />
          )}
          {symbol && (
            <ChartView
              ref={chartViewRef}
              symbol={symbol}
              token={token}
              interval={interval}
              period={period}
              chartType={chartType}
              candlestickColors={candlestickColors}
              lineConfig={lineConfig}
              barColor={barConfig.color}
              activeTA={activeTA}
              comparisonList={comparisonList}
              comparisonMode={comparisonMode}
              view={view}
              activeTool={activeTool}
              moveEnabled={moveEnabled}
              drawingConfigs={drawingConfigs}
              chartBundleUrl={props.chartBundleUrl}
              onLoadingChange={props.onLoadingChange}
              onNoData={() =>
                props.onNoData?.({
                  type: WidgetMessageType.NoData,
                  source: WidgetNameSource.AdvanceChart,
                  data: null,
                })
              }
              onError={(err) => props.onError?.(err)}
              onDrawMenuChange={(menu) => {
                if (!comparisonMode) setDrawMenu(menu);
              }}
            />
          )}
        </div>
      </div>
    </Layout>
  );
};

const AdvanceChartWidget: React.FC<AdvanceChartWidgetProps> = (props) => (
  <ChartSettingsProvider>
    <AdvanceChartWidgetInner {...props} />
  </ChartSettingsProvider>
);

export default AdvanceChartWidget;
